import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface GameOverScreenProps {
  type: 'won' | 'lost';
  message: string | null;
  onPlayAgain: () => void;
  onCreateNew: () => void;
  customButton?: {
    text: string;
    onClick: () => void;
  };
  currentLevel?: number;
  highestLevel?: number;
  rescuedEmojis?: Record<string, number>; // Collection of rescued emojis with counts
  themeName?: string;
  themeDescription?: string;
}

const GameOverScreen: React.FC<GameOverScreenProps> = ({
  type,
  message,
  onPlayAgain,
  onCreateNew,
  customButton,
  currentLevel,
  highestLevel,
  rescuedEmojis,
  themeName,
  themeDescription
}) => {
  const [showGameOverButtons, setShowGameOverButtons] = useState(false);
  const navigate = useNavigate();
  const { id, sessionId } = useParams();
  const isWin = type === 'won';
  const defaultMessage = isWin 
    ? 'Hats off! You have saved them all.'
    : 'Never Mind.. Mission not yet over!';
  const title = isWin ? '🫡 You Saved them all!! 🎖️' : currentLevel ? 'Escaped on Level '+currentLevel : "Oops.. Escaped!!";
  const color = isWin ? '#4CAF50' : '#FF5722';

  // Add achievement message for auto mode losses
  const achievementMessage = !isWin && currentLevel && highestLevel ? (
    <div style={{ 
      marginBottom: '10px', 
      padding: '10px', 
      backgroundColor: 'rgba(255, 193, 7, 0.1)', 
      borderRadius: '5px',
      border: '1px solid rgba(255, 193, 7, 0.3)'
    }}>
      <h4 style={{ 
        fontSize: '1.5rem', 
        color: '#FFC107',
        margin: '5px 0'
      }}>
        🏆 Successfully completed 
        <span style={{ color: 'rgb(233, 30, 99)' }}> {highestLevel} </span>
         levels 🎖️
      </h4>
    </div>
  ) : null;

  // Rescued Emojis Collection Display
  const rescuedEmojiCollection = rescuedEmojis && Object.keys(rescuedEmojis).length > 0 ? (
    <div style={{
      marginBottom: '15px',
      padding: '10px 10px 6px 10px',
      backgroundColor: 'rgba(76, 175, 80, 0.08)',
      borderRadius: '10px',
      border: '2px solid rgba(76, 175, 80, 0.18)',
      maxWidth: 420,
      marginLeft: 'auto',
      marginRight: 'auto',
    }}>
      <h4 style={{
        fontSize: '1.1rem',
        color: '#4CAF50',
        margin: '0 0 8px 0',
        textAlign: 'center',
        fontWeight: 600
      }}>
        🎯 Emoji Collection
      </h4>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 4,
        minHeight: 36,
        maxHeight: 'min(180px, 30vh, 40vw)',
        overflowY: 'auto',
      }}>
        {Object.entries(rescuedEmojis)
          .sort(([,a], [,b]) => b - a)
          .map(([emoji, count]) => (
            <span
              key={emoji}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '1.25rem',
                background: 'rgba(255,255,255,0.18)',
                borderRadius: '999px',
                padding: '2px 10px 2px 8px',
                color: '#222',
                border: '1px solid #FFC107',
                fontWeight: 600,
                boxShadow: '0 1px 4px rgba(76,175,80,0.07)',
                minWidth: 0,
                lineHeight: 1.1
              }}
            >
              <span style={{ fontSize: '1.35em', marginRight: 4 }}>{emoji}</span>
              <span style={{
                background: '#FFC107',
                color: '#222',
                borderRadius: '999px',
                fontSize: '0.85em',
                fontWeight: 700,
                padding: '0 7px',
                marginLeft: 2,
                marginRight: -2,
                minWidth: 18,
                textAlign: 'center',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
              }}>×{count}</span>
            </span>
        ))}
      </div>
      <div style={{
        textAlign: 'center',
        marginTop: '4px',
        fontSize: '0.95rem',
        color: '#4CAF50',
        fontWeight: 'bold'
      }}>
        Total Rescued: {Object.values(rescuedEmojis).reduce((sum, count) => sum + count, 0)}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        color: message ? color : '#FFC107',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        zIndex: 1000,
        animation: 'fadeIn 0.5s ease-in-out',
        minWidth: '300px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>

        <h4 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>{title}</h4>

        {/* ACHIEVEMENT MESSAGE */}
        {achievementMessage}
        {rescuedEmojiCollection}

        <p style={{ fontSize: '1.2rem'}}>{message || defaultMessage}</p>

        {/* THEME NAME & DESCRIPTION */}
        {themeName && (
          <div style={{ fontSize: '1.2em', color: '#E91E63', marginBottom: 6, fontWeight: 600 }}>{themeName}</div>
        )}
        {themeDescription && (
          <div style={{
            marginBottom: 18,
            color: '#fff',
            background: 'rgba(233,30,99,0.12)',
            borderRadius: 8,
            padding: '10px 18px',
            fontSize: '1.1em',
            fontWeight: 500,
            maxWidth: 400,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(233,30,99,0.08)',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {themeDescription}
          </div>
        )}

        {/* Share Button - only show if there are rescued emojis */}
        {rescuedEmojis && Object.keys(rescuedEmojis).length > 0 && (
          <button
            onClick={() => {
              // Navigate to the completed URL
              const isAutoMode = id === 'auto' || !!sessionId;
              const actualGameId = isAutoMode ? sessionId : id;
              const completedUrl = isAutoMode 
                ? `/game/auto/${actualGameId}/completed`
                : `/game/${actualGameId}/completed`;
              navigate(completedUrl);
            }}
            style={{
              padding: '10px 25px',
              fontSize: '1.1rem',
              backgroundColor: '#E91E63',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              margin: '10px',
              fontWeight: 'bold',
              boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            📸 Share Achievement
          </button>
        )}

        {!customButton && (
          <button 
            style={{
              padding: '15px 40px',
              fontSize: '1.5rem',
              backgroundColor: '#FFC107',
              border: 'none',
              borderRadius: '30px',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              minWidth: '200px',
              minHeight: '60px',
              margin: '10px'
            }} 
            onClick={onPlayAgain}
          >
            Try Again
          </button>
        )}
        
        {customButton && (
          <button 
            style={{
              padding: '15px 40px',
              fontSize: '1.5rem',
              backgroundColor: '#FFC107',
              border: 'none',
              borderRadius: '30px',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              minWidth: '200px',
              minHeight: '60px',
              margin: '10px auto',
              display: 'block'
            }} 
            onClick={customButton.onClick}
          >
            {customButton.text}
          </button>
        )}
      </div>

      {!customButton && (
        <>
          <div style={{ 
            position: 'fixed',
            bottom: '40px',
            right: '40px',
            zIndex: 20,
            touchAction: 'none'
          }}>
            <button 
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#FFC107',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 2s infinite',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)'
              }}
              onClick={() => setShowGameOverButtons(!showGameOverButtons)}
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span style={{ fontSize: '2.5em' }}>🛸</span>
            </button>
          </div>

          {showGameOverButtons && (
            <div style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)', 
              textAlign: 'center', 
              zIndex: 5000,
              color: '#FFC107', 
              touchAction: 'none' 
            }}>
              <button 
                style={{
                  padding: '15px 40px',
                  fontSize: '1.5rem',
                  backgroundColor: '#FFC107',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  minWidth: '200px',
                  minHeight: '60px',
                  margin: '10px'
                }} 
                onClick={onPlayAgain}
              >
                Play Again
              </button>
              <button 
                style={{
                  padding: '15px 40px',
                  fontSize: '1.5rem',
                  backgroundColor: '#FFC107',
                  border: 'none',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  minWidth: '200px',
                  minHeight: '60px',
                  margin: '10px',
                  whiteSpace: 'nowrap'
                }} 
                onClick={onCreateNew}
              >
                Create New
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default GameOverScreen; 